// TODO: MOVE IT TO JOB LISTING FEATURE

import {
  Button,
  ButtonV2,
  HiristLoader,
  Select as SelectElement,
} from '@jobseeker/lotus/elements';
import {TooltipStyled} from '@jobseeker/lotus/elements/Tooltip';
import {Box, Grid, InputAdornment, styled, Typography} from '@mui/material';
import {useSelector} from 'react-redux';

import {
  categories,
  categoryOptions,
  experienceOptionsHirist,
  experiences,
  HIRIST_FUNCTIONAL_AREAS,
  locations,
  methodOptions,
  PopularTags,
  postings,
  postingsHirist,
  postingsJobfeed,
  searchTypes,
  sortBy,
} from '@jobseeker/lotus/components/JobSearchBar/data';

import TextField from '@jobseeker/lotus/elements/Input';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {SearchIconGrey, WhiteCrossOutlined} from '@jobseeker/lotus/icons';
import type {AppState} from '@jobseeker/common-core/types';
import {handleTracking, deferTask} from '@jobseeker/common-core/utils';
import {locationMap} from '@jobseeker/lotus/utils';
import {Image} from '@jobseeker/lotus/components/Image';
import {memo, useCallback, useEffect, useRef, useState} from 'react';
import {PiSlidersFill} from 'react-icons/pi';
import {JobSearchBarProps} from '../type';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';

const FilterIcon = styled(PiSlidersFill)(() => ({
  height: '16px',
  width: '16px',
  transform: 'rotate(-90deg)',
}));
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const getRandomItems = (arr: any[], count: number) => {
  const shuffled = [...arr].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

const Listbox = styled('div')<any>(({theme, variant}: any) => ({
  width: '98%',
  marginLeft: variant === VersionVariants.VERSION_ONE ? '0.5%' : '0.5%',
  zIndex: 10,
  position: 'sticky',
  listStyle: 'none',
  // padding: '8px 0',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#000',
  overflow: 'auto',
  maxHeight: 200,
  border: '1px solid',
  borderColor:
    variant === VersionVariants.VERSION_ONE ? '#bbb' : theme.palette.divider,
  borderRadius: variant === VersionVariants.VERSION_ONE ? 6 : 16,
  marginTop: 6,
  '& li.Mui-focused': {
    backgroundColor: '#4a8df6',
    color: 'white',
    cursor: 'pointer',
  },
  '& li:active': {
    backgroundColor: '#2977f5',
    color: 'white',
  },
}));

const List = styled('li')(() => ({
  padding: '4px 16px',
  fontSize: '14px',
  ':hover': {
    backgroundColor: !isAppIimjobs ? '#eff3fa' : '#91B87929',
  },
}));

const Select = (props: any) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const isLarge = useIsLargeDevice();
  const isHiristJobfeed = !isAppIimjobs && props.page === 'jobfeed';

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  useEffect(() => {
    const shownTimestamp = localStorage.getItem(
      `${props.name}-tooltip-shown-timestamp`
    );
    const now = Date.now();

    if (
      (!shownTimestamp || now - Number(shownTimestamp) > 24 * 60 * 60 * 1000) &&
      window.localStorage
    ) {
      const shown = localStorage.getItem(`${props.name}-tooltip-shown`);
      if (props.tooltip || (!shown && props.tooltip)) {
        handleTooltipOpen();
        if (!props.tooltip.persist) {
          localStorage.setItem(
            `${props.name}-tooltip-shown-timestamp`,
            String(now)
          );
          window.dispatchEvent(new Event('experience-tooltip-updated'));
        }
      }
    }

    window.addEventListener('click', () => {
      handleTooltipClose();
      window.dispatchEvent(new Event('experience-tooltip-updated'));
    });

    if (!isHiristJobfeed) {
      let scrollTimeout: any = null;
      window.addEventListener('scroll', () => {
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        scrollTimeout = setTimeout(() => {
          handleTooltipClose();
        }, 200); // adjust the offset time as needed (200ms in this example)
      });
    } else {
      window.addEventListener('scroll', handleTooltipClose);
    }
  }, []);

  const onTooltipCloseClick = () => {
    handleTooltipClose();
    if (isLarge) {
      localStorage.setItem(`${props.name}-tooltip-shown`, 'true');
      window.dispatchEvent(new Event('experience-tooltip-updated'));
    }
  };
  if (props.tooltip && isLarge) {
    return (
      <TooltipStyled
        isGrey={isAppIimjobs ? false : true}
        title={
          <Box
            display={'flex'}
            width={'auto'}
            justifyContent={'center'}
            alignItems={'flex-start'}
            gap={1}
          >
            <Typography fontWeight={500} fontSize={isAppIimjobs ? '11px' : 14}>
              {props.tooltip.title}
            </Typography>
            <Image
              style={{cursor: 'pointer'}}
              onClick={e => {
                e.stopPropagation();
                onTooltipCloseClick();
              }}
              src={WhiteCrossOutlined}
              width={12}
              height={12}
              alt=""
            />
          </Box>
        }
        // onClose={handleTooltipClose}
        open={tooltipOpen}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        placement="bottom-start"
        arrow
        background={!isAppIimjobs && '#676765'}
        PopperProps={isHiristJobfeed ? {disablePortal: true} : undefined}
        TransitionProps={isHiristJobfeed ? {timeout: 0} : undefined}
      >
        <Box>
          <SelectElement {...props} />
        </Box>
      </TooltipStyled>
    );
  } else {
    return <SelectElement {...props} />;
  }
};

interface SearchQueryFieldProps {
  externalQuery: string;
  onDebouncedChange: (e: any, value?: any) => void;
  onClick: (e: any) => void;
  variant: any;
  inputRef: any;
  textFieldSx: any;
  showAutocomplete: boolean;
  autocompleteList: any[];
  onAutoCompleteListClick: (value: string) => void;
  openSkills: boolean;
  autoSkills: boolean;
  isLarge: boolean;
  listboxVariant: any;
}

const SearchQueryField = memo(function SearchQueryField({
  externalQuery,
  onDebouncedChange,
  onClick,
  variant,
  inputRef,
  textFieldSx,
  showAutocomplete,
  autocompleteList,
  onAutoCompleteListClick,
  openSkills,
  autoSkills,
  isLarge,
  listboxVariant,
}: SearchQueryFieldProps) {
  const theme = useWrappedTheme();
  const sanitizeQuery = (value?: string) => (value === '-' ? '' : value || '');
  const [localQuery, setLocalQuery] = useState(sanitizeQuery(externalQuery));
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocalQuery(sanitizeQuery(externalQuery));
  }, [externalQuery]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const onlySpecialCharRegex = /^[^a-zA-Z0-9]+$/;
  const isError =
    !!localQuery &&
    (onlySpecialCharRegex.test(localQuery) || !localQuery.trim());

  const onChange = useCallback(
    (e: any, value?: any) => {
      const val = e.target.value;
      setLocalQuery(val);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onDebouncedChange(e, value);
      }, 200);
    },
    [onDebouncedChange]
  );

  return (
    <>
      <TextField
        inputRef={inputRef}
        size="small"
        onClick={onClick}
        placeholder="Enter skills/designations/companies"
        variant={variant}
        aria-autocomplete="list"
        aria-expanded="true"
        autoComplete="off"
        spellCheck="false"
        aria-invalid="false"
        name="query"
        onChange={onChange}
        error={isError}
        helperText={
          isError ? 'Only special character and spaces are not allowed' : ''
        }
        sx={textFieldSx}
        value={localQuery}
        InputProps={{
          style: {height: '39px'},
          startAdornment: (
            <InputAdornment position="start">
              <Image
                src={SearchIconGrey}
                alt={'Search'}
                width={18}
                height={18}
              />
            </InputAdornment>
          ),
        }}
      />
      {showAutocomplete && autocompleteList?.length > 0 && (
        <Listbox variant={listboxVariant} sx={{position: 'absolute'}}>
          {autocompleteList.map((keyword, index) => (
            <List
              style={{cursor: 'default'}}
              key={`searchform-keyword-${index}`}
              onClick={() => onAutoCompleteListClick(keyword)}
            >
              {keyword}
            </List>
          ))}
        </Listbox>
      )}
      {!isAppIimjobs &&
        !showAutocomplete &&
        isLarge &&
        localQuery.length < 2 &&
        (openSkills || autoSkills) && (
          <Box
            position={'absolute'}
            zIndex={10}
            sx={{
              backgroundColor: '#f7f7f7',
              border: '1px solid #ebebeb',
              width: '100%',
              py: 2,
              height: 90,
              minWidth: 360,
              maxWidth: 420,
              px: 1,
              borderRadius: 2,
            }}
          >
            <Typography sx={{opacity: 0.6, fontSize: 14}}>
              Popular Designations/Skills
            </Typography>
            <Box display={'flex'} mt={1}>
              {getRandomItems(PopularTags[0].tags, 4).map((e: any) => (
                <Button
                  component={'a'}
                  href={`/search/${e.name}`}
                  sx={{
                    border: `1px solid ${theme.palette.primary.main} !important`,
                    borderRadius: '16px',
                    padding: '4px 4px',
                    mr: 1,
                    fontSize: 12,
                    pt: 0.5,
                    display: 'flex',
                    height: 30,
                    zIndex: 1200,
                    textDecoration: 'none',
                    alignItems: 'center',
                    transition: 'background-color 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.main}`,
                      color: '#fff !important',
                    },
                  }}
                >
                  {e.alias.length > 12
                    ? e.alias.slice(0, 9) + '...'
                    : e.alias}{' '}
                </Button>
              ))}
            </Box>
          </Box>
        )}
    </>
  );
});

export default function (props: JobSearchBarProps) {
  const isFilterBar = props.isFilter;
  const isCompactJobfeedFilters = isAppIimjobs && props?.pageName === 'jobfeed';
  const theme = useWrappedTheme();
  const {
    showAutocomplete,
    onAutoCompleteSelect,
    autocompleteList,
    showPosting = false,
  } = props;
  const [menuOpen, setOpen] = useState<any>(false);
  const [openSkills, setOpenSkills] = useState<any>(false);

  const textBarRef = useRef();

  const setMenuOpen = (val: any) => {
    // console.log(v, menuOpen);
    const value = val;
    deferTask(() => {
      if (value && value === menuOpen) {
        setMenuOpen(false);
      } else setOpen(value);
    });
  };

  const submitFilter = () => {
    // Immediate UI feedback - keep synchronous
    setMenuOpen(false);
    // Defer heavy work for better INP
    const processFilter = () => {
      const modifiedData: any = {};
      if (
        Array.isArray(props?.formValue?.loc) &&
        props?.formValue?.loc.length > 0
      ) {
        modifiedData.locationLabels = locationMap(props.formValue?.loc);
      }
      if (
        Array.isArray(props?.formValue?.sort) &&
        props?.formValue?.sort.length > 0
      ) {
        modifiedData.sort = props?.formValue?.sort?.map((s: string | number) =>
          sortBy.find(sb => sb.value == s)?.label.toLowerCase()
        );
      }

      try {
        if (!isAppIimjobs) {
          if (typeof location === 'undefined') {
            return;
          }

          const queryParams = new URLSearchParams(location.href);

          handleTracking('jsClickSearchButton', {
            event_category: 'Search',
            Origin: location?.pathname ?? '',
            UserId: sessionStorage.getItem('userId') || 'NA',
            Device: sessionStorage.getItem('device') || 'desktop',
            Ref: queryParams.get('ref') ?? '',
            Type: 'NA',
            Keyword: props?.formValue?.query ?? '',
            Exp: props?.formValue?.experience ?? '',
            Location: props?.formValue?.loc,
            PostingTime: 'NA',
          });
        }
      } catch (err) {
        console.error('Error while sending jsClickSearchButton GA event', err);
      }

      props.onFilter && props.onFilter({...props.formValue, ...modifiedData});
    };
    deferTask(processFilter);
  };
  const [load, setLoad] = useState<boolean>(false);
  const [hover, setHover] = useState(false);
  const getBackgroundColor = (hover: any) =>
    hover ? theme.palette.primary.main : 'transparent';
  const handleClick = () => {
    setLoad(true);
    submitFilter();
    setTimeout(() => setLoad(false), 4000);
  };
  const isLarge = useIsLargeDevice();

  const eventCallback = (e: MouseEvent) => {
    if (e?.target?.name === 'query') {
    } else {
      if (onAutoCompleteSelect) {
        onAutoCompleteSelect('');
        setOpenSkills(false);
      }
    }
    if (e?.target?.getAttribute('data-name') === 'navbar-search') {
      setMenuOpen(false);
    }
  };
  useEffect(() => {
    window?.addEventListener('click', eventCallback);

    return () => {
      window.removeEventListener('click', eventCallback);
    };
  }, []);

  const onFilterChange = useCallback(
    (evt: any, val?: any) => {
      const e = evt;
      const value = val;
      deferTask(() => {
        if (props.onChange) {
          props.onChange(e, value);
        }
      });
    },
    [props.onChange]
  );

  const onFormSubmit = (e: any) => {
    e.preventDefault();
    deferTask(() => {
      submitFilter();
    });
  };
  const textClick = (e: any) => {
    deferTask(() => {
      setMenuOpen(false);
      setOpenSkills(true);
    });
    // textBarRef.current.focus();
  };
  const {isLoading} = useSelector((state: AppState) => state.job);
  // const isLoading = false;

  const onAutoCompleteListClick = (value: string) => {
    onFilterChange({target: {name: 'query', value}}, value);
    if (onAutoCompleteSelect) {
      onAutoCompleteSelect(value);
      setOpenSkills(false);
    }
  };
  const onlySpecialCharRegex = /^[^a-zA-Z0-9]+$/;
  const currentQuery = props?.formValue?.query || '';
  const isInputDisabled =
    props.showSearchInput &&
    (onlySpecialCharRegex.test(currentQuery) || !currentQuery.trim());

  const hasOtherFiltersApplied =
    !!props?.formValue?.query?.trim() ||
    (Array.isArray(props?.formValue?.experience) &&
      props?.formValue?.experience.length > 0) ||
    (Array.isArray(props?.formValue?.loc) &&
      props?.formValue?.loc.length > 0) ||
    (Array.isArray(props?.formValue?.sort) &&
      props?.formValue?.sort.length > 0) ||
    (Array.isArray(props?.formValue?.posting) &&
      props?.formValue?.posting.length > 0) ||
    (Array.isArray(props?.formValue?.functionalArea) &&
      props?.formValue?.functionalArea.length > 0) ||
    (Array.isArray(props?.formValue?.industry) &&
      props?.formValue?.industry.length > 0);

  const isCategoryDefault =
    !isAppIimjobs &&
    props?.pageName === 'women_at_work' &&
    !hasOtherFiltersApplied &&
    (!props?.formValue?.category ||
      (Array.isArray(props?.formValue?.category) &&
        props?.formValue?.category?.[0] === 0));

  const css_props: any = props.inline
    ? {
        flexDirection: {
          xxs: props.onlySeachInput ? 'row' : 'column',
          // md: 'row',
          lg: 'row',
        },
        width: {xxs: 12, lg: 5.5},
        fontSize: 12,
        sort: {lg: false},
        button: (
          <ButtonV2
            type="button"
            variant="contained"
            disabled={isInputDisabled}
            sx={{
              marginTop: {xxs: 2, md: 0},
              height: '39px',
              zIndex: 2,
              width: {xxs: '100%', md: '100%'},
              fontSize: {xxs: 12, xs: isAppIimjobs ? 16 : 14},
              lineHeight: {xxs: '12px', xs: '16px'},
              px: {xxs: 1, md: 2.5},
              py:
                props.variant === VersionVariants.VERSION_HIRIST
                  ? 0.75
                  : {xxs: 1.5, md: 1},
              backgroundColor: isAppIimjobs
                ? 'secondary.main'
                : theme.palette.primary.main,
              '&:hover': {
                backgroundColor: isAppIimjobs
                  ? 'theme.palette.primary.main'
                  : theme.palette.primary.main,
              },
            }}
            onClick={submitFilter}
          >
            {isFilterBar ? (isAppIimjobs ? 'Filter' : 'Apply') : 'Search'}
          </ButtonV2>
        ),
      }
    : {
        flexDirection: {
          xxs: props.onlySeachInput ? 'row' : 'column',
          lg: 'row',
          md: 'column',
        },
        width: {xxs: 12, lg: 4},
        fontSize: 14,
        sort: {lg: true},
        button: isAppIimjobs ? (
          <ButtonV2
            type="button"
            variant="outlined"
            // disabled={isInputDisabled}
            color={
              props.variant === VersionVariants.VERSION_HIRIST
                ? 'primary'
                : 'secondary'
            }
            sx={{
              zIndex: 2,
              width: {xxs: 'fit-content', md: '100%'},
              fontSize: {xxs: 12, xs: isAppIimjobs ? 16 : 14},
              lineHeight: {xxs: '12px', xs: '16px'},
              px: {xxs: 1, md: 2.5},
              py:
                props.variant === VersionVariants.VERSION_HIRIST
                  ? 0.75
                  : {xxs: 1, md: 1},
              pl: {xxs: 1.5, md: 2.5},
              '.MuiButton-startIcon': {
                mr: {xxs: 0.5, md: 1},
              },
              ':hover': {
                backgroundColor:
                  props.variant === VersionVariants.VERSION_HIRIST
                    ? 'primary.main'
                    : 'secondary.main',
                borderColor:
                  props.variant === VersionVariants.VERSION_HIRIST
                    ? 'primary.main'
                    : 'secondary.main',
                '.searchbar-icon': {
                  filter: 'brightness(10)',
                },
                color: '#fff',
                '*': {
                  color: '#fff',
                },
              },
            }}
            onClick={submitFilter}
          >
            {isFilterBar ? (isAppIimjobs ? 'Filter' : 'Apply') : 'Search'}
          </ButtonV2>
        ) : (
          <Button
            type="button"
            onClick={submitFilter}
            sx={{
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: '16px',
              padding: '2px 24px',
              paddingTop: '5px',
              display: 'flex',
              zIndex: 1200,
              alignItems: 'center',
              transition: 'background-color 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.primary.main, // Prevents hover background change
                border: `1px solid ${theme.palette.primary.main}`, // Keeps the same border
                color: '#fff !important',
              },
            }}
          >
            <Typography
              className="text"
              sx={{
                fontFamily: 'Nunito Sans',
                fontSize: 14,
                fontWeight: 500,
                transition: 'color 0.3s ease-in-out',
                '&:hover': {
                  color: '#fff !important',
                },
              }}
            >
              Apply
            </Typography>
          </Button>
        ),
      };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      isInputDisabled ? () => {} : submitFilter();
    }
  };
  const experienceOptions = isAppIimjobs
    ? experiences
    : experienceOptionsHirist;

  const experienceValue =
    props?.formValue?.experience?.[0] === 1 && isAppIimjobs
      ? undefined
      : props?.formValue?.experience;

  const isValid = experienceOptions.some(
    opt => opt.value === experienceValue?.[0]
  );

  const finalExperienceValue = isValid ? experienceValue : undefined;

  return (
    <Grid
      component={'form'}
      onSubmit={onFormSubmit}
      container
      spacing={{xxs: 1}}
      sx={{flexDirection: css_props.flexDirection}}
      flexWrap={{xxs: 'nowrap', lg: 'unset'}}
      onKeyDown={handleKeyPress}
    >
      {!isFilterBar && props.showSearchInput && (
        <Grid
          item
          xxs={isAppIimjobs ? css_props.width.xxs : 12}
          md={isAppIimjobs ? css_props.width.md : 12}
          lg={isAppIimjobs ? css_props.width.lg : 8}
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: {xxs: 'column', md: 'row'},
            gap: 1,
          }}
        >
          {!isAppIimjobs && props.isTypeDropDown === true && (
            <Grid
              item
              xxs={12}
              md={2}
              lg={4}
              sx={
                props?.hideLocationBar
                  ? {
                      display: 'flex',
                      justifyContent: 'end',
                    }
                  : {}
              }
            >
              <Select
                menuOpen={menuOpen === 'searchType'}
                setMenuOpen={setMenuOpen}
                variant={
                  isAppIimjobs || props.inline
                    ? props.variant
                    : VersionVariants.VERSION_FOUR
                }
                options={searchTypes}
                label={'Jobs'}
                value={props?.formValue?.searchType}
                name="searchType"
                onChange={onFilterChange}
                tooltip={props?.tooltip?.searchType}
                MenuProps={{
                  disablePortal: true,
                }}
              />
            </Grid>
          )}
          <Grid item xxs={12} md lg position="relative">
            <SearchQueryField
              externalQuery={currentQuery}
              onDebouncedChange={onFilterChange}
              onClick={textClick}
              variant={props.variant}
              inputRef={textBarRef}
              textFieldSx={{
                zIndex: isAppIimjobs ? 3 : 0,
                fontSize: 14,
                '.MuiInputBase-root': {
                  fontSize:
                    VersionVariants.VERSION_ONE === props.variant
                      ? 16
                      : css_props.fontSize,
                },
              }}
              showAutocomplete={!!showAutocomplete}
              autocompleteList={autocompleteList || []}
              onAutoCompleteListClick={onAutoCompleteListClick}
              openSkills={openSkills}
              autoSkills={!!props?.autoSkills}
              isLarge={isLarge}
              listboxVariant={props.variant}
            />
          </Grid>
        </Grid>
      )}
      <Grid
        item
        xxs={isAppIimjobs ? true : 12}
        md={isAppIimjobs ? undefined : 12}
        lg={isAppIimjobs ? undefined : 8}
        {...css_props.sort}
      >
        <Grid container spacing={{xxs: 1}} sx={{alignItems: 'center'}}>
          {isFilterBar &&
            props.showSortBy &&
            !props.onlySeachInput &&
            isAppIimjobs && (
              <Grid item xxs={12} sm md>
                <Select
                  menuOpen={menuOpen === 'sort'}
                  setMenuOpen={setMenuOpen}
                  variant={
                    isAppIimjobs || props.inline
                      ? props.variant
                      : VersionVariants.VERSION_FOUR
                  }
                  options={sortBy}
                  label={'Sort By'}
                  value={props?.formValue?.sort}
                  tooltip={props?.tooltip?.sort}
                  name="sort"
                  onChange={onFilterChange}
                />
              </Grid>
            )}

          <>
            {props.showIndustry &&
              !(isAppIimjobs && props?.pageName === 'women_at_work') && (
                <Grid item xxs={12} sm md>
                  <Select
                    // menuOpen={menuOpen === 'loc'}
                    // setMenuOpen={setMenuOpen}
                    variant={VersionVariants.VERSION_THREE}
                    options={props.industryOptions}
                    label={'Industry'}
                    name="industry"
                    value={props?.formValue?.industry}
                    multiple={true}
                    onChange={onFilterChange}
                  />
                </Grid>
              )}
            {!props.onlySeachInput &&
              (props.showCategory ||
                props.formValue?.searchType?.[0] === 1 ||
                props.formValue?.searchType?.[0] === 2) && (
                <Grid item xxs={12} sm md>
                  <Select
                    menuOpen={menuOpen === 'category'}
                    setMenuOpen={setMenuOpen}
                    variant={
                      isAppIimjobs || props.inline
                        ? props.variant
                        : VersionVariants.VERSION_FOUR
                    }
                    options={isAppIimjobs ? categories : categoryOptions}
                    label={'Category'}
                    page={props?.pageName}
                    value={props?.formValue?.category}
                    name="category"
                    onChange={onFilterChange}
                    tooltip={props?.tooltip?.category}
                    MenuProps={{
                      disablePortal: true,
                    }}
                  />
                </Grid>
              )}
            {!props.onlySeachInput &&
              !isAppIimjobs &&
              (props.showFunctionalArea ||
                props.formValue?.searchType?.[0] === 1 ||
                props.formValue?.searchType?.[0] === 2) && (
                <Grid item xxs={12} sm md>
                  <Select
                    menuOpen={menuOpen === 'functionalArea'}
                    setMenuOpen={setMenuOpen}
                    variant={
                      isAppIimjobs || props.inline
                        ? props.variant
                        : VersionVariants.VERSION_FOUR
                    }
                    options={HIRIST_FUNCTIONAL_AREAS}
                    label={'Functional Area'}
                    value={props?.formValue?.functionalArea}
                    name="functionalArea"
                    onChange={onFilterChange}
                    tooltip={props?.tooltip?.functionalArea}
                    MenuProps={{
                      disablePortal: true,
                    }}
                  />
                </Grid>
              )}
            {!props.onlySeachInput &&
              !isAppIimjobs &&
              props.formValue?.searchType?.[0] !== 1 && (
                <Grid
                  item
                  xxs={12}
                  sm
                  md
                  sx={
                    props?.hideLocationBar
                      ? {
                          display: 'flex',
                          justifyContent: 'end',
                        }
                      : {}
                  }
                >
                  <Select
                    menuOpen={menuOpen === 'experience'}
                    setMenuOpen={setMenuOpen}
                    maxWidth={
                      props?.hideLocationBar ? (isAppIimjobs ? 220 : 150) : 0
                    }
                    variant={
                      isAppIimjobs || props.inline
                        ? props.variant
                        : VersionVariants.VERSION_FOUR
                    }
                    options={
                      isAppIimjobs ? experiences : experienceOptionsHirist
                    }
                    tooltip={props?.tooltip?.experience}
                    value={
                      isAppIimjobs
                        ? finalExperienceValue
                        : props?.formValue?.experience
                    }
                    page={props?.pageName}
                    label={isAppIimjobs ? 'Experience' : 'Any Exp. Level'}
                    name="experience"
                    onChange={onFilterChange}
                    MenuProps={{
                      disablePortal: true,
                    }}
                  />
                </Grid>
              )}
            {!props.onlySeachInput &&
              !props?.hideLocationBar &&
              props.formValue?.searchType?.[0] !== 2 && (
                <Grid
                  item
                  xxs={12}
                  sm={isCompactJobfeedFilters ? 'auto' : true}
                  md={isCompactJobfeedFilters ? 'auto' : true}
                  sx={
                    isCompactJobfeedFilters
                      ? {minWidth: {sm: 180, md: 190, xl: 240}}
                      : undefined
                  }
                >
                  <Select
                    menuOpen={menuOpen === 'loc'}
                    setMenuOpen={setMenuOpen}
                    variant={
                      isAppIimjobs || props.inline
                        ? props.variant
                        : VersionVariants.VERSION_FOUR
                    }
                    options={locations}
                    page={props?.pageName}
                    tooltip={props?.tooltip?.loc}
                    label={isAppIimjobs ? 'Locations' : 'Any Location'}
                    name="loc"
                    value={props?.formValue?.loc}
                    multiple={true}
                    menuMinWidth={
                      isCompactJobfeedFilters
                        ? {xxs: 180, sm: 180, md: 190, xl: 240}
                        : undefined
                    }
                    onChange={onFilterChange}
                    MenuProps={{
                      disablePortal: true,
                    }}
                  />
                </Grid>
              )}
            {!props.onlySeachInput && !!isAppIimjobs && (
              <Grid
                item
                xxs={12}
                sm={isCompactJobfeedFilters ? 'auto' : true}
                md={isCompactJobfeedFilters ? 'auto' : true}
                sx={
                  props?.hideLocationBar
                    ? {
                        display: 'flex',
                        justifyContent: 'end',
                        ...(isCompactJobfeedFilters
                          ? {minWidth: {sm: 140, md: 145, xl: 180}}
                          : {}),
                      }
                    : isCompactJobfeedFilters
                    ? {minWidth: {sm: 140, md: 145, xl: 180}}
                    : {}
                }
              >
                <Select
                  menuOpen={menuOpen === 'experience'}
                  setMenuOpen={setMenuOpen}
                  maxWidth={
                    props?.hideLocationBar ? (isAppIimjobs ? 220 : 150) : 0
                  }
                  variant={
                    isAppIimjobs || props.inline
                      ? props.variant
                      : VersionVariants.VERSION_FOUR
                  }
                  options={isAppIimjobs ? experiences : experienceOptionsHirist}
                  tooltip={props?.tooltip?.experience}
                  page={props?.pageName}
                  value={
                    isAppIimjobs
                      ? finalExperienceValue
                      : props?.formValue?.experience
                  }
                  label={isAppIimjobs ? 'Experience' : 'Any Exp. Level'}
                  name="experience"
                  menuMinWidth={
                    isCompactJobfeedFilters
                      ? {xxs: 140, sm: 140, md: 145, xl: 180}
                      : undefined
                  }
                  onChange={onFilterChange}
                  MenuProps={{
                    disablePortal: true,
                  }}
                />
              </Grid>
            )}
            {((!isFilterBar && !props.onlySeachInput && isAppIimjobs) ||
              (!isFilterBar &&
                !props.onlySeachInput &&
                !isAppIimjobs &&
                props.formValue?.searchType?.[0] !== 1 &&
                props.formValue?.searchType?.[0] !== 2) ||
              (!isAppIimjobs &&
                !props.hideLocationBar &&
                props.formValue?.searchType?.[0] !== 1 &&
                props.formValue?.searchType?.[0] !== 2) ||
              (isAppIimjobs && props?.pageName === 'jobfeed')) &&
              showPosting && (
                <Grid
                  item
                  xxs={12}
                  sm={isCompactJobfeedFilters ? 'auto' : true}
                  md={isCompactJobfeedFilters ? 'auto' : true}
                  sx={
                    isCompactJobfeedFilters
                      ? {minWidth: {sm: 140, md: 145, xl: 180}}
                      : undefined
                  }
                >
                  <Select
                    menuOpen={menuOpen === 'posting'}
                    setMenuOpen={setMenuOpen}
                    variant={
                      isAppIimjobs || props.inline
                        ? props.variant
                        : VersionVariants.VERSION_FOUR
                    }
                    options={
                      isCompactJobfeedFilters
                        ? postingsJobfeed
                        : isAppIimjobs
                        ? postings
                        : postingsHirist
                    }
                    label={'Posting'}
                    value={props?.formValue?.posting}
                    name="posting"
                    menuMinWidth={
                      isCompactJobfeedFilters
                        ? {xxs: 140, sm: 140, md: 145, xl: 180}
                        : undefined
                    }
                    onChange={onFilterChange}
                    MenuProps={{
                      disablePortal: true,
                    }}
                    tooltip={props?.tooltip?.posting}
                  />
                </Grid>
              )}
            {!isAppIimjobs && props?.formValue?.searchType?.[0] === 2 && (
              <Grid item xxs={12} sm md>
                <Select
                  menuOpen={menuOpen === 'method'}
                  setMenuOpen={setMenuOpen}
                  variant={
                    isAppIimjobs || props.inline
                      ? props.variant
                      : VersionVariants.VERSION_FOUR
                  }
                  options={methodOptions}
                  label={'Method'}
                  value={props?.formValue?.method}
                  name="method"
                  onChange={onFilterChange}
                  tooltip={props?.tooltip?.method}
                  MenuProps={{
                    disablePortal: true,
                  }}
                />
              </Grid>
            )}
          </>
          <Grid
            item
            xxs={12}
            sm={props.inline ? true : 'auto'}
            md={props.inline ? true : 'auto'}
            sx={{
              paddingLeft: {md: 2},
            }}
          >
            {isAppIimjobs || props.inline ? (
              props.variant === VersionVariants.VERSION_ONE ? (
                <Button
                  type="button"
                  variant="contained"
                  sx={{
                    px: 4,
                    fontSize: isAppIimjobs ? '18px' : 14,
                    width: '100%',
                  }}
                  size="small"
                  disabled={isInputDisabled}
                  onClick={submitFilter}
                >
                  {isFilterBar ? (isAppIimjobs ? 'Filter' : 'op') : 'Search'}
                </Button>
              ) : (
                css_props.button
              )
            ) : (
              <Button
                type="button"
                onClick={isCategoryDefault ? undefined : handleClick}
                sx={{
                  border: `1px solid ${theme.palette.primary.main} !important`,
                  borderRadius: '16px',
                  padding: '4px 24px',
                  pt: 0.5,
                  display: 'flex',
                  height: 30,
                  width: 86,
                  zIndex: 1200,
                  alignItems: 'center',
                  transition: 'background-color 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main, // Prevents hover background change
                    border: `1px solid ${theme.palette.primary.main}`, // Keeps the same border
                    color: '#fff !important',
                  },
                }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
              >
                <Typography
                  className="text"
                  sx={{
                    fontFamily: 'Nunito Sans',
                    fontSize: 14,
                    fontWeight: 500,
                    transition: 'color 0.3s ease-in-out',
                    '&:hover': {
                      color: '#fff !important',
                    },
                  }}
                >
                  {!load || !isLoading
                    ? 'Apply'
                    : HiristLoader(getBackgroundColor(hover))}
                </Typography>
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
    // </form>
  );
}
